import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { getBlogPostingJsonLd } from "@/utils/seo";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <section className="w-full pt-28 pb-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-file-search-line text-accent-600 text-2xl"></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Article Not Found
                </h1>
                <p className="text-foreground-500 mb-8">
                  The article you are looking for does not exist or has been moved.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-line"></i>
                  Back to Home
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getBlogPostingJsonLd(article)) }}
      />
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="w-full pt-24 md:pt-28 pb-10 md:pb-14 bg-background-100/50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  Home
                </Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-700 whitespace-nowrap">{article.category}</span>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <p className="text-xs md:text-sm text-foreground-500 font-medium tracking-wide mb-3">
                / ACBUYSPREADSHEET {article.category.toUpperCase()}
              </p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                {article.intro}
              </p>

              {/* Author + meta */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center">
                    <span className="text-accent-700 text-base font-heading font-bold">A</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">ACBuySpreadsheet Team</p>
                    <p className="text-xs text-foreground-500">{article.date} &middot; {article.readTime}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100/70 text-primary-700 text-xs font-semibold rounded-full">
                  <i className="ri-star-fill text-accent-500 text-xs"></i>
                  {article.rating}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article featured image */}
        <section className="w-full pb-6 md:pb-10 bg-background-100/50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="aspect-[16/8] rounded-2xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`ACBuySpreadsheet — ${article.title}`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article content */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <article className="max-w-3xl mx-auto">
              <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-8">

                {/* Intro paragraph */}
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Introduction
                  </h2>
                  <p className="mb-4">
                    {article.intro}
                  </p>
                  <p>
                    Continue reading to explore every dimension of this topic. For a broader overview, visit the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline cursor-pointer">ACBuySpreadsheet home page</Link> where we cover feature comparisons and additional resources.
                  </p>
                </div>

                {/* Dynamic sections */}
                {article.sections.map((section, sIdx) => {
                  if (section.type === "h2") {
                    return (
                      <div key={sIdx}>
                        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                          {section.title}
                        </h2>
                        {section.body?.map((p, pIdx) => (
                          <p key={pIdx} className="mb-4">{p}</p>
                        ))}
                        {section.items && (
                          <div className="space-y-3 mb-4">
                            {section.items.map((item, iIdx) => (
                              <div key={iIdx} className="flex gap-3 pl-4 border-l-2 border-primary-300">
                                <div>
                                  <strong className="text-foreground-900">{item.title}</strong>
                                  <p className="text-foreground-500 mt-1">{item.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (section.type === "table") {
                    return (
                      <div key={sIdx}>
                        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                          {section.title}
                        </h2>
                        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden mb-6">
                          <div className="grid grid-cols-3 bg-primary-500 text-background-50 px-4 py-3 text-xs font-semibold">
                            <div>Feature</div>
                            <div className="text-center">Traditional</div>
                            <div className="text-center">ACBuySpreadsheet</div>
                          </div>
                          {section.rows?.map((row, rIdx) => (
                            <div key={rIdx} className={`grid grid-cols-3 px-4 py-2.5 text-xs border-b border-background-200/50 last:border-b-0 ${rIdx % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}`}>
                              <div className="font-medium text-foreground-800">{row.feature}</div>
                              <div className="text-center text-foreground-500">{row.trad}</div>
                              <div className="text-center text-foreground-800 font-medium">{row.acbuy}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (section.type === "steps") {
                    return (
                      <div key={sIdx}>
                        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                          {section.title}
                        </h2>
                        <ol className="space-y-4 mb-4 ml-0 list-none">
                          {section.steps?.map((step, stIdx) => (
                            <li key={stIdx} className="flex gap-4">
                              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">
                                {step.step}
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-foreground-900 mb-1">{step.title}</h4>
                                <p className="text-xs md:text-sm text-foreground-500">{step.desc}</p>
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    );
                  }

                  if (section.type === "tips") {
                    return (
                      <div key={sIdx}>
                        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                          {section.title}
                        </h2>
                        <div className="space-y-3 mb-4">
                          {section.tips?.map((tip, tIdx) => (
                            <div key={tIdx} className="flex gap-3 items-start">
                              <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                              <p className="text-sm text-foreground-600">{tip}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (section.type === "conclusion") {
                    return (
                      <div key={sIdx}>
                        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                          {section.title}
                        </h2>
                        {section.body?.map((p, pIdx) => (
                          <p key={pIdx} className="mb-4">{p}</p>
                        ))}
                      </div>
                    );
                  }

                  return null;
                })}

                {/* FAQ */}
                {article.faqs.length > 0 && (
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                      Frequently Asked Questions
                    </h2>
                    <div className="space-y-4">
                      {article.faqs.map((faq, fIdx) => (
                        <div key={fIdx} className="bg-background-100/70 rounded-xl p-4 md:p-5 border border-background-200/60">
                          <h4 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">
                            {faq.q}
                          </h4>
                          <p className="text-xs md:text-sm text-foreground-600">
                            {faq.a}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conclusion + Internal Links */}
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Ready to Explore More?
                  </h2>
                  <p className="mb-4">
                    The <strong>acbuyspreadsheet</strong> ecosystem offers a wealth of resources for data-driven professionals. Whether you are just getting started or looking to master advanced techniques, there is always more to learn.
                  </p>
                  <p className="mb-6">
                    Return to the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline cursor-pointer">ACBuySpreadsheet home page</Link> for feature comparisons, category navigation, and additional resources to accelerate your data optimization journey.
                  </p>

                  {/* Internal links navigation */}
                  <div className="bg-primary-100/50 rounded-xl p-5 border border-primary-200/50">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-3">
                      Continue Reading:
                    </h4>
                    <ul className="space-y-2">
                      <li>
                        <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-2">
                          <i className="ri-home-line text-xs"></i>
                          ACBuySpreadsheet Home — Full Feature Overview
                        </Link>
                      </li>
                      {articles
                        .filter((a) => a.slug !== article.slug)
                        .slice(0, 3)
                        .map((a) => (
                          <li key={a.slug}>
                            <Link to={`/article/${a.slug}`} className="text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer flex items-center gap-2">
                              <i className="ri-article-line text-xs"></i>
                              {a.title}
                            </Link>
                          </li>
                        ))}
                      <li>
                        <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer flex items-center gap-2">
                          <i className="ri-external-link-line text-xs"></i>
                          Visit Main Site — Browse Products
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}